"use client";

import React from "react";
import { usePathname, useSearchParams } from "next/navigation";
import GoogleConsentControls from "./GoogleConsentControls";
import {
  applyGoogleTracking,
  buildGoogleConsentStorageKey,
  GOOGLE_MANAGED_CONVERSION_PENDING_CHANGE_EVENT,
  getBrowserLocalStorage,
  hasAnyGoogleTracking,
  hasConfiguredAdvertising,
  hasPendingManagedGoogleAdsConversion,
  readStoredGoogleConsent,
  resolveConsentBootstrap,
  resolveGoogleTrackingConfig,
  shouldSuppressGoogleTracking,
  trackBookingConversion,
  writeStoredGoogleConsent,
  type GoogleConsentPreferences,
  type OpeningsTrackingSurface,
} from "@/lib/googleTracking";
import { bookingFlowApi } from "@/lib/api/bookingFlowApi";

type TenantAnalyticsProps = {
  platformMeasurementId?: string | null;
  platformGtmId?: string | null;
  tenantGoogleTagId?: string | null;
  googleAdsConversionId?: string | null;
  googleAdsBookingConversionLabel?: string | null;
  developerId?: string | null;
  tenantId?: string | null;
  openingsSurface?: OpeningsTrackingSurface | null;
  requireConsent?: boolean;
  /**
   * Tenant opt-in that pre-grants advertising storage where no consent prompt
   * is owed. Without it Google Ads cannot match an ad click to a booking.
   */
  defaultAdvertisingConsent?: boolean;
  /** Server-derived presence only; the signed HttpOnly value is never hydrated. */
  managedAttributionSessionPresent?: boolean;
  /** Server-issued only after an ambiguous create; never inferred in-browser. */
  managedAttributionResumeEligible?: boolean;
};

type ConsentState = {
  namespace: string | null;
  effectiveConsentRequired: boolean | null;
  preferences: GoogleConsentPreferences;
  hasExplicitDecision: boolean;
  shouldPrompt: boolean;
  ready: boolean;
};

const INITIAL_CONSENT_STATE: ConsentState = {
  namespace: null,
  effectiveConsentRequired: null,
  preferences: { analytics: false, advertising: false },
  hasExplicitDecision: false,
  shouldPrompt: false,
  ready: false,
};

export default function TenantAnalytics({
  platformMeasurementId,
  platformGtmId,
  tenantGoogleTagId,
  googleAdsConversionId,
  googleAdsBookingConversionLabel,
  developerId,
  tenantId,
  openingsSurface,
  requireConsent = false,
  defaultAdvertisingConsent = false,
  managedAttributionSessionPresent = false,
  managedAttributionResumeEligible = false,
}: TenantAnalyticsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const [hostname, setHostname] = React.useState("");
  const [embedded, setEmbedded] = React.useState(false);
  const [browserReady, setBrowserReady] = React.useState(false);
  const [consentState, setConsentState] = React.useState<ConsentState>(
    INITIAL_CONSENT_STATE,
  );
  const [managedPendingState, setManagedPendingState] = React.useState<{
    namespace: string | null;
    pending: boolean;
  }>({ namespace: null, pending: false });
  const resumeAttemptedForNamespaceRef = React.useRef<string | null>(null);

  const trackingConfig = React.useMemo(
    () =>
      resolveGoogleTrackingConfig(
        {
          platformMeasurementId,
          platformGtmId,
          tenantGoogleTagId,
          googleAdsConversionId,
          googleAdsBookingConversionLabel,
          developerId,
          tenantId,
          openingsSurface,
        },
        hostname,
      ),
    [
      developerId,
      googleAdsBookingConversionLabel,
      googleAdsConversionId,
      hostname,
      openingsSurface,
      platformGtmId,
      platformMeasurementId,
      tenantGoogleTagId,
      tenantId,
    ],
  );
  const advertisingConfigured = hasConfiguredAdvertising(trackingConfig);
  const managedPendingReady =
    browserReady &&
    managedPendingState.namespace === trackingConfig.cookiePrefix;
  const managedConversionPending =
    managedPendingReady && managedPendingState.pending;
  // Middleware rewrites pretty manage/quote links internally. Once hydrated,
  // use the address-bar URL so suppression cannot depend on the rewritten
  // pathname/search values exposed by Next navigation hooks.
  const suppressionPathname =
    browserReady && typeof window !== "undefined"
      ? window.location.pathname
      : pathname;
  const suppressionSearch =
    browserReady && typeof window !== "undefined"
      ? window.location.search
      : search;
  const suppressed = shouldSuppressGoogleTracking({
    pathname: suppressionPathname,
    search: suppressionSearch,
    embedded,
  });
  const managedAttributionAdvertisingConfigured =
    managedAttributionSessionPresent && !suppressed;
  const managedPendingAdvertisingConfigured =
    managedConversionPending && !suppressed;
  const effectiveConsentRequired =
    requireConsent ||
    advertisingConfigured ||
    managedAttributionAdvertisingConfigured ||
    managedPendingAdvertisingConfigured;
  const consentReady =
    consentState.ready &&
    consentState.namespace === trackingConfig.cookiePrefix &&
    consentState.effectiveConsentRequired === effectiveConsentRequired;

  React.useEffect(() => {
    let isEmbedded = false;
    try {
      isEmbedded = window.self !== window.top;
    } catch {
      isEmbedded = true;
    }

    setHostname(window.location.hostname);
    setEmbedded(isEmbedded);
    setBrowserReady(true);
  }, [pathname]);

  React.useEffect(() => {
    if (!browserReady) return;

    const updatePendingState = () => {
      const pending = hasPendingManagedGoogleAdsConversion(trackingConfig);
      setManagedPendingState((current) =>
        current.namespace === trackingConfig.cookiePrefix &&
        current.pending === pending
          ? current
          : { namespace: trackingConfig.cookiePrefix, pending },
      );
    };
    updatePendingState();
    window.addEventListener(
      GOOGLE_MANAGED_CONVERSION_PENDING_CHANGE_EVENT,
      updatePendingState,
    );
    return () =>
      window.removeEventListener(
        GOOGLE_MANAGED_CONVERSION_PENDING_CHANGE_EVENT,
        updatePendingState,
      );
  }, [browserReady, trackingConfig]);

  React.useEffect(() => {
    if (!browserReady || !managedPendingReady) return;

    const storage = getBrowserLocalStorage();
    const storedPreferences = readStoredGoogleConsent(
      storage,
      trackingConfig.cookiePrefix,
    );
    const bootstrap = resolveConsentBootstrap({
      storedPreferences,
      effectiveConsentRequired,
      defaultAdvertisingConsent,
    });
    setConsentState({
      ...bootstrap,
      namespace: trackingConfig.cookiePrefix,
      effectiveConsentRequired,
      ready: true,
    });
  }, [
    advertisingConfigured,
    browserReady,
    defaultAdvertisingConsent,
    effectiveConsentRequired,
    managedPendingReady,
    trackingConfig.cookiePrefix,
  ]);

  React.useEffect(() => {
    if (!browserReady || !managedPendingReady) return;
    const storage = getBrowserLocalStorage();
    if (!storage) return;

    const onStorage = (event: StorageEvent) => {
      const consentStorageKey = buildGoogleConsentStorageKey(
        trackingConfig.cookiePrefix,
      );
      if (
        event.storageArea !== storage ||
        (event.key && event.key !== consentStorageKey)
      ) {
        return;
      }

      const storedPreferences = readStoredGoogleConsent(
        storage,
        trackingConfig.cookiePrefix,
      );
      const bootstrap = resolveConsentBootstrap({
        storedPreferences,
        effectiveConsentRequired,
        defaultAdvertisingConsent,
      });
      setConsentState({
        ...bootstrap,
        namespace: trackingConfig.cookiePrefix,
        effectiveConsentRequired,
        ready: true,
      });
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [
    advertisingConfigured,
    browserReady,
    defaultAdvertisingConsent,
    effectiveConsentRequired,
    managedPendingReady,
    trackingConfig.cookiePrefix,
  ]);

  React.useEffect(() => {
    if (!browserReady || !managedPendingReady || !consentReady) return;
    applyGoogleTracking(trackingConfig, consentState.preferences, suppressed);
  }, [
    browserReady,
    consentReady,
    consentState.preferences,
    managedPendingReady,
    suppressed,
    trackingConfig,
  ]);

  React.useEffect(() => {
    if (
      !browserReady ||
      !managedPendingReady ||
      !consentReady ||
      suppressed ||
      !managedAttributionSessionPresent ||
      !managedAttributionResumeEligible ||
      resumeAttemptedForNamespaceRef.current === trackingConfig.cookiePrefix
    ) {
      return;
    }

    resumeAttemptedForNamespaceRef.current = trackingConfig.cookiePrefix;
    let cancelled = false;
    void bookingFlowApi
      .resumeAdsAutopilotAttribution()
      .then((result) => {
        if (cancelled || !result.data.conversion) return;
        const conversion = result.data.conversion;
        trackBookingConversion({
          conversionEventId: conversion.eventId,
          value: conversion.value,
          currency: conversion.currency,
          ...(conversion.managedGoogleAds
            ? { managedGoogleAds: conversion.managedGoogleAds }
            : {}),
        });
      })
      .catch(() => {
        // The server-owned cookies remain intact after upstream ambiguity. A
        // later document may retry without exposing the signed session here.
      });
    return () => {
      cancelled = true;
    };
  }, [
    browserReady,
    consentReady,
    managedPendingReady,
    managedAttributionResumeEligible,
    managedAttributionSessionPresent,
    suppressed,
    trackingConfig.cookiePrefix,
  ]);

  const commitConsent = React.useCallback(
    (preferences: GoogleConsentPreferences) => {
      writeStoredGoogleConsent(
        getBrowserLocalStorage(),
        preferences,
        trackingConfig.cookiePrefix,
      );
      setConsentState({
        namespace: trackingConfig.cookiePrefix,
        effectiveConsentRequired,
        preferences,
        hasExplicitDecision: true,
        shouldPrompt: false,
        ready: true,
      });
    },
    [effectiveConsentRequired, trackingConfig.cookiePrefix],
  );

  if (
    !browserReady ||
    !managedPendingReady ||
    !consentReady ||
    suppressed ||
    (!hasAnyGoogleTracking(trackingConfig) &&
      !managedAttributionAdvertisingConfigured &&
      !managedPendingAdvertisingConfigured)
  ) {
    return null;
  }

  return (
    <GoogleConsentControls
      preferences={consentState.preferences}
      needsDecision={
        consentState.shouldPrompt && !consentState.hasExplicitDecision
      }
      onCommit={commitConsent}
    />
  );
}
